import "./Dialog.css";
import React, {
	type ComponentProps,
	type ComponentType,
	createContext,
	type PropsWithChildren,
	Suspense,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useEventCallback } from "usehooks-ts";
import { ButtonIcon } from "../../components/ButtonIcon";
import { ContentLoading } from "../../components/ContentLoading";

interface DialogData<T extends object> {
	component: ComponentType<T>;
	props: PropsWithChildren<T>;
}

type DialogState<T extends object> = DialogData<T> | undefined;

const DialogContext = createContext<{
	open: <T extends object>(component: ComponentType<T>, props: T) => void;
	close: () => void;
}>({
	open: () => {
		throw new Error("DialogContext not initialized");
	},
	close: () => {
		throw new Error("DialogContext not initialized");
	},
});

function DialogProvider(props: PropsWithChildren) {
	const ref = useRef<HTMLDialogElement>(null);
	const [state, setState] = useState<DialogState<object>>(undefined);

	const open = useEventCallback(
		<T extends object>(component: ComponentType<T>, props: T) => {
			setState({ component, props } as unknown as DialogState<object>);
			ref.current?.showModal();
		},
	);

	const close = useEventCallback(() => {
		setState(undefined);
		ref.current?.close();
	});

	useEffect(() => {
		const elem = ref.current;
		if (!elem) {
			return;
		}

		const controller = new AbortController();

		elem.addEventListener("close", () => setState(undefined), {
			signal: controller.signal,
		});

		return () => controller.abort();
	}, []);

	return (
		<DialogContext.Provider value={{ open, close }}>
			{props.children}
			<dialog aria-modal="true" ref={ref}>
				<Suspense fallback={<ContentLoading />}>
					{state && React.createElement(state.component, state.props)}
				</Suspense>
			</dialog>
		</DialogContext.Provider>
	);
}

export function useDialog() {
	return useContext(DialogContext);
}

function Dialog(props: ComponentProps<"div">) {
	return <div {...props} />;
}

function DialogHeader(props: PropsWithChildren<{ title: string }>) {
	const dialog = useDialog();

	return (
		<header>
			<div>{props.title}</div>
			<ButtonIcon icon="close" onClick={() => dialog.close()} />
		</header>
	);
}

function DialogBody(props: PropsWithChildren) {
	return <main>{props.children}</main>;
}

function DialogFooter(props: ComponentProps<"footer">) {
	return <footer {...props} />;
}

const DialogNamespace = Object.assign(Dialog, {
	Provider: DialogProvider,
	Header: DialogHeader,
	Body: DialogBody,
	Footer: DialogFooter,
});

export { DialogNamespace as Dialog };
